import React, { useState, useEffect } from 'react';
import { FiGitMerge, FiLayers, FiDownload, FiChevronRight, FiZap } from 'react-icons/fi';
import { getProjects } from '../../api/projects';
import { getArchitecturesByProject } from '../../api/architectures';

// ─── Couches Médaillon ───────────────────────────────────────────────────────

interface LayerDef {
  id: string;
  label: string;
  emoji: string;
  color: string;
  bg: string;
  border: string;
  keywords: string[];
}

const LAYERS: LayerDef[] = [
  {
    id: 'bronze',
    label: 'Bronze',
    emoji: '🟫',
    color: '#92400E',
    bg: '#FEF3C7',
    border: '#F59E0B',
    keywords: ['bronze', 'raw', 'ingest', 'landing', 'source', 'ingestion'],
  },
  {
    id: 'silver',
    label: 'Silver',
    emoji: '⬜',
    color: '#374151',
    bg: '#F3F4F6',
    border: '#9CA3AF',
    keywords: ['silver', 'clean', 'refined', 'transform', 'staging', 'process'],
  },
  {
    id: 'gold',
    label: 'Gold',
    emoji: '🟡',
    color: '#78350F',
    bg: '#FEF9EE',
    border: '#D97706',
    keywords: ['gold', 'serve', 'agg', 'curated', 'business', 'mart'],
  },
  {
    id: 'reporting',
    label: 'Reporting',
    emoji: '📊',
    color: '#1E40AF',
    bg: '#EFF6FF',
    border: '#2563EB',
    keywords: ['report', 'bi', 'viz', 'publish', 'dashboard', 'presentation'],
  },
];

const ITEM_ICONS: Record<string, string> = {
  fabric_lakehouse:      '/icons/fabric/lakehouse_64_item.svg',
  fabric_warehouse:      '/icons/fabric/data_warehouse_64_item.svg',
  fabric_sql_database:   '/icons/fabric/sql_database_64_item.svg',
  fabric_pipeline:       '/icons/fabric/pipeline_64_item.svg',
  fabric_dataflow:       '/icons/fabric/dataflow_gen2_64_item.svg',
  fabric_notebook:       '/icons/fabric/notebook_64_item.svg',
  fabric_semantic_model: '/icons/fabric/semantic_model_64_item.svg',
  fabric_report:         '/icons/fabric/power_bi_48_color.svg',
  fabric_ml_model:       '/icons/fabric/data_science_48_color.svg',
  fabric_data_agent:     '/icons/fabric/data_agent_64_item.svg',
  fabric_eventstream:    '/icons/fabric/eventstream_64_item.svg',
  fabric_eventhouse:     '/icons/fabric/event_house_64_item.svg',
  fabric_kql_database:   '/icons/fabric/kql_database_64_item.svg',
  fabric_onelake:        '/icons/fabric/one_lake_48_color.svg',
};

const FABRIC_ITEM_TYPES = new Set(Object.keys(ITEM_ICONS));

// ─── Modèles de données ──────────────────────────────────────────────────────

interface LineageItem {
  id: string;
  type: string;
  label: string;
}

interface LineageWorkspace {
  id: string;
  label: string;
  layer: string;        // bronze | silver | gold | reporting | unknown
  capacityLabel: string;
  items: LineageItem[];
}

interface LineageEdge {
  sourceId: string;
  targetId: string;
}

interface ArchitectureLineage {
  archId: string;
  archName: string;
  projectName: string;
  workspaces: LineageWorkspace[];
  edges: LineageEdge[];        // edges workspace→workspace explicites du canvas
  isMedallion: boolean;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function detectLayer(label: string): string {
  const name = label.toLowerCase();
  for (const layer of LAYERS) {
    if (layer.keywords.some(k => name.includes(k))) return layer.id;
  }
  return 'unknown';
}

function layerOrder(layerId: string): number {
  const order: Record<string, number> = { bronze: 0, silver: 1, gold: 2, reporting: 3, unknown: 4 };
  return order[layerId] ?? 4;
}

// ─── Export OpenLineage JSON ─────────────────────────────────────────────────

function toOpenLineage(arch: ArchitectureLineage): object {
  return {
    eventType: 'COMPLETE',
    eventTime: new Date().toISOString(),
    producer: 'GenIaC',
    schemaURL: 'https://openlineage.io/spec/1-0-5/OpenLineage.json',
    run: { runId: arch.archId },
    job: { namespace: arch.projectName, name: arch.archName },
    inputs: arch.workspaces
      .filter(ws => ws.layer === 'bronze')
      .map(ws => ({ namespace: arch.projectName, name: ws.label, facets: { schema: { fields: [] } } })),
    outputs: arch.workspaces
      .filter(ws => ws.layer === 'gold' || ws.layer === 'reporting')
      .map(ws => ({ namespace: arch.projectName, name: ws.label })),
  };
}

function downloadOpenLineage(arch: ArchitectureLineage) {
  const blob = new Blob([JSON.stringify(toOpenLineage(arch), null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `lineage-${arch.archName.toLowerCase().replace(/\s+/g, '-')}.json`;
  a.click();
  URL.revokeObjectURL(url);
}

// ─── Sous-composants ─────────────────────────────────────────────────────────

const WorkspaceCard: React.FC<{ ws: LineageWorkspace }> = ({ ws }) => {
  const layerDef = LAYERS.find(l => l.id === ws.layer);
  const bg = layerDef?.bg ?? '#F9FAFB';
  const border = layerDef?.border ?? '#E5E7EB';
  const color = layerDef?.color ?? '#6B7280';
  const label = layerDef ? `${layerDef.emoji} ${layerDef.label}` : '• Autre';

  return (
    <div
      className="flex flex-col rounded-xl overflow-hidden shadow-sm flex-shrink-0"
      style={{ border: `1.5px solid ${border}`, minWidth: 180, maxWidth: 220 }}
    >
      {/* Layer badge */}
      <div className="px-3 py-1.5 text-xs font-bold" style={{ background: bg, color }}>
        {label}
      </div>
      {/* Workspace name */}
      <div className="px-3 py-2 bg-white border-b" style={{ borderColor: border }}>
        <div className="flex items-center gap-1.5">
          <img src="/icons/fabric/group_workspace_64_non-item.svg" alt=""
            className="w-4 h-4" onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
          <span className="text-xs font-semibold text-gray-800 truncate">{ws.label}</span>
        </div>
        {ws.capacityLabel && (
          <span className="text-xs text-gray-400 ml-5">{ws.capacityLabel}</span>
        )}
      </div>
      {/* Items */}
      <div className="px-3 py-2 bg-white flex-1 space-y-1 min-h-12">
        {ws.items.length === 0 && (
          <p className="text-xs text-gray-300 italic">Aucun item</p>
        )}
        {ws.items.map(item => (
          <div key={item.id} className="flex items-center gap-1.5">
            {ITEM_ICONS[item.type]
              ? <img src={ITEM_ICONS[item.type]} alt="" className="w-3.5 h-3.5 flex-shrink-0"
                  onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
              : <div className="w-3.5 h-3.5 rounded-sm flex-shrink-0" style={{ background: border }} />
            }
            <span className="text-xs text-gray-600 truncate">{item.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const ArchLineageCard: React.FC<{ arch: ArchitectureLineage }> = ({ arch }) => {
  const [expanded, setExpanded] = useState(true);

  // Trier les workspaces par couche (Bronze → Silver → Gold → Reporting → Unknown)
  const sorted = [...arch.workspaces].sort((a, b) => layerOrder(a.layer) - layerOrder(b.layer));

  // Grouper les workspaces unknown ensemble à la fin
  const flow = sorted.filter(ws => ws.layer !== 'unknown');
  const others = sorted.filter(ws => ws.layer === 'unknown');

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden mb-5">
      {/* Accent bar */}
      <div style={{ height: 3, background: arch.isMedallion ? 'linear-gradient(90deg, #F59E0B, #6366F1, #14B8A6)' : 'linear-gradient(90deg, #3B82F6, #6366F1)' }} />
      {/* Header */}
      <div
        className="px-6 py-4 border-b border-gray-100 flex items-center justify-between cursor-pointer hover:bg-gray-50"
        onClick={() => setExpanded(e => !e)}
      >
        <div className="flex items-center gap-3">
          <FiGitMerge size={16} className="text-blue-600" />
          <div>
            <span className="font-semibold text-gray-900">{arch.archName}</span>
            <span className="text-xs text-gray-400 ml-2">— {arch.projectName}</span>
          </div>
          {arch.isMedallion && (
            <span style={{ fontSize: 11, padding: '2px 10px', borderRadius: 12, background: 'linear-gradient(135deg, #92400E, #F59E0B)', color: 'white', fontWeight: 700, flexShrink: 0 }}>
              ✨ Médaillon
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={e => { e.stopPropagation(); downloadOpenLineage(arch); }}
            className="flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-lg bg-gray-50 text-gray-600 border border-gray-200 hover:bg-gray-100 transition-colors"
            title="Exporter en OpenLineage JSON"
          >
            <FiDownload size={11} />
            OpenLineage
          </button>
          <FiChevronRight
            size={16}
            className={`text-gray-400 transition-transform ${expanded ? 'rotate-90' : ''}`}
          />
        </div>
      </div>

      {expanded && (
        <div className="px-6 py-5 overflow-x-auto">
          {arch.workspaces.length === 0 ? (
            <p className="text-sm text-gray-400 italic">Aucun workspace dans cette architecture.</p>
          ) : (
            <>
              {/* Flux principal (couches détectées) */}
              {flow.length > 0 && (
                <div className="flex items-start gap-2 mb-4">
                  {flow.map((ws, idx) => (
                    <React.Fragment key={ws.id}>
                      <WorkspaceCard ws={ws} />
                      {idx < flow.length - 1 && (
                        <div style={{ display: 'flex', alignItems: 'center', alignSelf: 'center', flexShrink: 0, padding: '0 4px' }}>
                          <div style={{ width: 32, height: 2, background: 'linear-gradient(90deg, #D1D5DB, #9CA3AF)', borderRadius: 1 }} />
                          <FiChevronRight size={18} color="#9CA3AF" style={{ marginLeft: -6 }} />
                        </div>
                      )}
                    </React.Fragment>
                  ))}
                </div>
              )}

              {/* Workspaces non classifiés */}
              {others.length > 0 && (
                <div>
                  <p className="text-xs text-gray-400 mb-2 font-medium">Autres workspaces (couche non détectée)</p>
                  <div className="flex items-start gap-3 flex-wrap">
                    {others.map(ws => <WorkspaceCard key={ws.id} ws={ws} />)}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Composant principal ──────────────────────────────────────────────────────

const FabricLineageTab: React.FC = () => {
  const [architectures, setArchitectures] = useState<ArchitectureLineage[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const projects = await getProjects() as any[];
        const result: ArchitectureLineage[] = [];

        for (const proj of projects) {
          try {
            const archs = await getArchitecturesByProject(proj.id);
            for (const arch of archs) {
              const nodes: any[] = (arch.diagram_data as any)?.nodes || [];
              const edges: any[] = (arch.diagram_data as any)?.edges || [];

              // Capacités pour récupérer leur label
              const capMap = new Map<string, string>();
              nodes.filter(n => n.data?.type === 'fabric_capacity').forEach(cap => {
                capMap.set(cap.id, cap.data?.label || cap.data?.params?.display_name || 'Capacity');
              });

              const wsNodes = nodes.filter(n => n.data?.type === 'fabric_workspace');
              if (wsNodes.length === 0) continue;

              const workspaceMap = new Map<string, LineageWorkspace>();

              wsNodes.forEach(ws => {
                const wsLabel = ws.data?.label || ws.data?.params?.display_name || ws.data?.params?.name || 'Workspace';
                const capacityLabel = ws.parentNode ? capMap.get(ws.parentNode) ?? '' : '';

                // Items enfants directs ou dans un folder dans ce workspace
                const wsItems: LineageItem[] = nodes
                  .filter(n => {
                    const t = n.data?.type || n.data?.resourceType || '';
                    if (!FABRIC_ITEM_TYPES.has(t)) return false;
                    // Enfant direct
                    if (n.parentNode === ws.id) return true;
                    // Enfant d'un folder dans ce workspace
                    const parent = nodes.find(p => p.id === n.parentNode);
                    return parent?.parentNode === ws.id;
                  })
                  .map(n => ({
                    id: n.id,
                    type: n.data?.type || n.data?.resourceType || '',
                    label: n.data?.label || n.data?.params?.name || n.data?.type || '',
                  }));

                workspaceMap.set(ws.id, {
                  id: ws.id,
                  label: wsLabel,
                  layer: detectLayer(wsLabel),
                  capacityLabel,
                  items: wsItems,
                });
              });

              // Edges workspace→workspace explicites
              const wsEdges: LineageEdge[] = edges
                .filter(e => workspaceMap.has(e.source) && workspaceMap.has(e.target))
                .map(e => ({ sourceId: e.source, targetId: e.target }));

              const workspaces = Array.from(workspaceMap.values());
              const layers = new Set(workspaces.map(w => w.layer).filter(l => l !== 'unknown'));
              const isMedallion = layers.has('bronze') && layers.has('silver') && layers.has('gold');

              result.push({
                archId: arch.id,
                archName: arch.name,
                projectName: proj.name,
                workspaces,
                edges: wsEdges,
                isMedallion,
              });
            }
          } catch { /* non bloquant */ }
        }

        setArchitectures(result);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  const totalWorkspaces = architectures.reduce((s, a) => s + a.workspaces.length, 0);
  const medallionCount = architectures.filter(a => a.isMedallion).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <FiZap size={24} className="animate-pulse text-blue-500 mr-3" />
        <span className="text-gray-500">Analyse des architectures Fabric…</span>
      </div>
    );
  }

  if (architectures.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <FiLayers size={48} className="text-gray-300 mb-4" />
        <p className="text-gray-600 font-medium mb-2">Aucune architecture Fabric avec des workspaces</p>
        <p className="text-gray-400 text-sm">
          Créez une architecture avec des nœuds <strong>fabric_workspace</strong> pour visualiser le lineage.
        </p>
      </div>
    );
  }

  return (
    <div>
      {/* KPIs */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', marginBottom: '1.5rem' }}>
        <div style={{ background: 'linear-gradient(135deg, #1e40af 0%, #3B82F6 100%)', borderRadius: '1rem', padding: '1.5rem', color: 'white', boxShadow: '0 4px 16px rgba(59,130,246,0.3)', position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', top: -20, right: -20, width: 80, height: 80, background: 'rgba(255,255,255,0.1)', borderRadius: '50%' }} />
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
            <span style={{ fontSize: '0.78rem', fontWeight: 600, color: 'rgba(255,255,255,0.8)' }}>Architectures Fabric</span>
            <div style={{ background: 'rgba(255,255,255,0.2)', borderRadius: '0.5rem', padding: '0.4rem', fontSize: '1rem' }}>🗂️</div>
          </div>
          <div style={{ fontSize: '2.25rem', fontWeight: 800, marginBottom: '0.25rem' }}>{architectures.length}</div>
          <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.65)' }}>avec workspaces</div>
        </div>
        <div style={{ background: 'linear-gradient(135deg, #0f766e 0%, #14B8A6 100%)', borderRadius: '1rem', padding: '1.5rem', color: 'white', boxShadow: '0 4px 16px rgba(20,184,166,0.3)', position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', top: -20, right: -20, width: 80, height: 80, background: 'rgba(255,255,255,0.1)', borderRadius: '50%' }} />
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
            <span style={{ fontSize: '0.78rem', fontWeight: 600, color: 'rgba(255,255,255,0.8)' }}>Workspaces</span>
            <div style={{ background: 'rgba(255,255,255,0.2)', borderRadius: '0.5rem', padding: '0.4rem', fontSize: '1rem' }}>🏢</div>
          </div>
          <div style={{ fontSize: '2.25rem', fontWeight: 800, marginBottom: '0.25rem' }}>{totalWorkspaces}</div>
          <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.65)' }}>tous projets confondus</div>
        </div>
        <div style={{ background: 'linear-gradient(135deg, #92400E 0%, #F59E0B 100%)', borderRadius: '1rem', padding: '1.5rem', color: 'white', boxShadow: '0 4px 16px rgba(245,158,11,0.3)', position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', top: -20, right: -20, width: 80, height: 80, background: 'rgba(255,255,255,0.1)', borderRadius: '50%' }} />
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
            <span style={{ fontSize: '0.78rem', fontWeight: 600, color: 'rgba(255,255,255,0.8)' }}>Architectures Médaillon</span>
            <div style={{ background: 'rgba(255,255,255,0.2)', borderRadius: '0.5rem', padding: '0.4rem', fontSize: '1rem' }}>✨</div>
          </div>
          <div style={{ fontSize: '2.25rem', fontWeight: 800, marginBottom: '0.25rem' }}>{medallionCount}</div>
          <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.65)' }}>Bronze → Silver → Gold détecté</div>
        </div>
      </div>

      {/* Légende */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.25rem', padding: '0.75rem 1rem', background: 'white', border: '1px solid #e5e7eb', borderRadius: '0.75rem', flexWrap: 'wrap' }}>
        <span style={{ fontSize: '0.72rem', fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginRight: '0.25rem' }}>Couches</span>
        {LAYERS.map(l => (
          <div key={l.id} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '3px 10px', borderRadius: 20, background: l.bg, border: `1.5px solid ${l.border}` }}>
            <span style={{ fontSize: '0.75rem', fontWeight: 700, color: l.color }}>{l.emoji} {l.label}</span>
          </div>
        ))}
        <span style={{ fontSize: '0.72rem', color: '#9CA3AF', marginLeft: 'auto' }}>Couche déduite du nom du workspace</span>
      </div>

      {/* Architectures */}
      {architectures.map(arch => (
        <ArchLineageCard key={arch.archId} arch={arch} />
      ))}

      <div style={{ marginTop: '1.25rem', padding: '0.875rem 1.25rem', background: 'linear-gradient(135deg, #EFF6FF 0%, #F0FDF4 100%)', border: '1px solid #BFDBFE', borderRadius: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
        <FiGitMerge size={16} color="#3B82F6" style={{ flexShrink: 0 }} />
        <p style={{ fontSize: '0.78rem', color: '#374151', margin: 0 }}>
          Lineage déduit des noms de workspaces et des connexions canvas. Exportable au format <strong>OpenLineage</strong> (compatible Microsoft Purview / Apache Marquez).
        </p>
      </div>
    </div>
  );
};

export default FabricLineageTab;
